import type { Ref } from "vue"
import type { Media } from "@/models"
import type { ContentTypeV1, IReactionRequestV1 } from "@/types"
import { useStorage } from "@vueuse/core"
import { ref, watchEffect } from "vue"
import { MediaType } from "@/models"

export function useMediaReaction(mediaRef: Ref<Media | undefined>) {
  // storage for pending reactions
  const pendingReactions = useStorage("pendingReactions", {})

  const incrementedReactionCount = ref(0) // Tracks newly added reactions
  const currentReactionCount = ref(0) // Total reaction count from media
  const hasUserReacted = ref(false)
  const mediaId = ref(0)
  const mediaType = ref<ContentTypeV1>("photo")

  const initialiseMediaReaction = () => {
    const media = mediaRef.value

    currentReactionCount.value = media?.reactions?.Counts?.love || 0
    hasUserReacted.value
      = media?.reactions?.UserReactions?.includes("love") || false
    mediaId.value = media?.id ?? 0
    mediaType.value = media?.type === MediaType.Vid ? "video" : "photo"
  }

  watchEffect(initialiseMediaReaction)

  /**
   * Increments the reaction count and updates pending reactions.
   * @param count - number of reactions to add (default is 1).
   */
  const incrementTotalReactCount = (count: number = 1) => {
    currentReactionCount.value += count
    incrementedReactionCount.value += count

    // store the pending reaction for future processing if needed
    pendingReactions.value = {
      contentID: mediaId.value,
      contentType: mediaType.value,
      type: "love",
      quantity: incrementedReactionCount.value,
    }

    hasUserReacted.value = true
  }

  const clearReactionData = () => {
    incrementedReactionCount.value = 0
    pendingReactions.value = {}
  }

  return {
    incrementedReactionCount,
    currentReactionCount,
    hasUserReacted,
    mediaId,
    mediaType,
    pendingReactions:
      Object.keys(pendingReactions.value).length === 0
        ? undefined
        : (pendingReactions.value as IReactionRequestV1),
    incrementTotalReactCount,
    clearReactionData,
  }
}
